import React, { useCallback, useEffect, useMemo } from 'react';
import { DropDownConfig } from '@kemu-io/kemu-core/types';
import variablesManager, { getControlScopedVarName } from '@kemu-io/kemu-core/common/managers/variablesManager.js';
import { Dropdown, Menu, Tooltip } from 'antd';
import { DownOutlined } from '@ant-design/icons';
import classNames from 'classnames';
import { DataType } from '@kemu-io/hs-types';
import FieldLabel from '../FieldLabel/FieldLabel';
import styles from './DropdownField.module.css';
import FormGroup from '@components/form-control/formGroup/formGroup';
import useForceUpdate from '@hooks/useForceReload';

type Props = {
  label: string;
  variableName: string;
  recipeId: string;
  thingRecipeId: string;
  widgetId: string;
  color?: string;
  disabled?: boolean;
  isSingleElement: boolean;
  config: DropDownConfig;
  settingsIndex: number;
  onUpdateConfig: (settingsIndex: number, newConfig: DropDownConfig) => void;
}

type MenuInfo = {
  key: string;
}

const DropdownField = (props: Props) => {
  const { variableName, widgetId, disabled, isSingleElement, label, config, settingsIndex, onUpdateConfig } = props;
  const scopedVarName = getControlScopedVarName(variableName);
  const forceUpdate = useForceUpdate();

  const variableValue = variablesManager.getVariableValue({
    variableName: scopedVarName,
    readerWidgetId: widgetId,
  });

  const selectedOption = variableValue?.value as string | undefined ??  config.customValue ?? config.defaultValue;

  const handleMenuClick = useCallback((event: MenuInfo) => {
    if (event.key) {
      const option = String(event.key);
      variablesManager.setVariableValue({
        variableName: scopedVarName,
        value: option,
        setByWidgetId: widgetId,
        type: DataType.String,
      });

      onUpdateConfig(settingsIndex, {
        ...config,
        customValue: option,
      } as DropDownConfig);
    }
  }, [scopedVarName, widgetId, onUpdateConfig, settingsIndex, config]);

  const menu = useMemo(() => (
		<Menu className={styles.DropdownMenu} onClick={handleMenuClick} selectedKeys={selectedOption ? [selectedOption] : []}>
			{config.options.map((option) => (
				<Menu.Item key={`${option}`}>
					{option}
				</Menu.Item>
			))}
		</Menu>
	), [config.options, selectedOption, handleMenuClick]);

  // Subscribe to variable changes to trigger re-render
  useEffect(() => {
    const unsubscribe = variablesManager.onVariableValueChange({
      variableName: scopedVarName,
      type: DataType.Anything,
      widgetId: widgetId,
      onValueChange: forceUpdate,
    });

    return unsubscribe;
  }, [scopedVarName, widgetId, forceUpdate]);

  return (
    <FormGroup column={true} noMarginBottom>
      <FieldLabel color={props.color} label={props.label} />
      <Dropdown
        overlay={menu}
        placement="bottomLeft"
        trigger={['click']}
        disabled={disabled}
        className={classNames({
          [styles.SingleDropDown]: isSingleElement && !label,
        })}
      >
        <div className={styles.DropDownTrigger}>
          <Tooltip title={selectedOption} mouseEnterDelay={0.6}>
            <span className={styles.SelectedOption} >
              {selectedOption}
            </span>
          </Tooltip>
          <DownOutlined
            className={styles.DropIcon}
          />
        </div>
      </Dropdown>
    </FormGroup>
  );
};

export default DropdownField;
